import * as React from 'react';

interface UseAssetSelectionOptions {
  /**
   * Ids of the assets shown now, in grid order. Shift ranges follow this order, "Select visible" takes all of them,
   * and ids that leave it drop out of the selection.
   */
  orderedIds: string[];
  /**
   * Selection starts over whenever this changes: pass everything that changes what is shown, like the collection,
   * filters and sort (SDD D59), because "Select visible" depends on it.
   */
  resetKey: string;
}

interface SelectionState {
  resetKey: string;
  ids: ReadonlySet<string>;
  /** Last asset clicked without Shift, where a Shift range starts. */
  anchorId: string | null;
}

const emptyState = (resetKey: string): SelectionState => ({ resetKey, ids: new Set(), anchorId: null });

/**
 * Multi-select state for an asset grid: click toggles one asset, Shift-click sets the whole range from the last
 * clicked asset to the same state, like in Classic and file managers (SDD D41).
 *
 * @example
 * const selection = useAssetSelection({ orderedIds: assets.map((a) => a.id), resetKey: `${collectionId}|${sort}` });
 * <Asset.Checkbox isSelected={selection.isSelected(id)} onSelectedChange={(_, { shiftKey }) => selection.toggle(id, { shiftKey })} />
 */
function useAssetSelection({ orderedIds, resetKey }: UseAssetSelectionOptions) {
  const [state, setState] = React.useState<SelectionState>(() => emptyState(resetKey));
  // A stale key means what is shown changed: read as empty right away, without a render that shows old selection
  const current = state.resetKey === resetKey ? state : emptyState(resetKey);

  const visibleIds = React.useMemo(() => new Set(orderedIds), [orderedIds]);
  const selectedIds = React.useMemo(
    () => new Set([...current.ids].filter((id) => visibleIds.has(id))),
    [current.ids, visibleIds],
  );

  const toggle = React.useCallback(
    (id: string, { shiftKey = false }: { shiftKey?: boolean } = {}) => {
      setState((previous) => {
        const base = previous.resetKey === resetKey ? previous : emptyState(resetKey);
        const ids = new Set(base.ids);
        const nextIsSelected = !ids.has(id);
        const anchorIndex = base.anchorId ? orderedIds.indexOf(base.anchorId) : -1;
        const targetIndex = orderedIds.indexOf(id);

        if (shiftKey && anchorIndex !== -1 && targetIndex !== -1) {
          const [from, to] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex];
          for (const rangeId of orderedIds.slice(from, to + 1)) {
            if (nextIsSelected) ids.add(rangeId);
            else ids.delete(rangeId);
          }
          // The anchor stays, so the next Shift-click still measures from the first click
          return { resetKey, ids, anchorId: base.anchorId };
        }

        if (nextIsSelected) ids.add(id);
        else ids.delete(id);
        return { resetKey, ids, anchorId: id };
      });
    },
    [orderedIds, resetKey],
  );

  const selectVisible = React.useCallback(() => {
    setState((previous) => ({
      resetKey,
      ids: new Set(orderedIds),
      anchorId: previous.resetKey === resetKey ? previous.anchorId : null,
    }));
  }, [orderedIds, resetKey]);

  const clear = React.useCallback(() => setState(emptyState(resetKey)), [resetKey]);

  const isSelected = React.useCallback((id: string) => selectedIds.has(id), [selectedIds]);

  return { selectedIds, isSelected, toggle, selectVisible, clear };
}

export { useAssetSelection };
export type { UseAssetSelectionOptions };
